import { useState, useEffect, type ReactNode } from 'react';
import { useApi } from '../../api';
import {
  fmtTime, fmtDuration,
  consolidatedLabel, consolidatedTone,
  consolidateSummary, CONSOLIDATED_BUCKETS,
  todayStr, thisMonthStr,
} from './statusUtils';
import { PresenceTag, DayTypeTag } from './AttendanceTags';
import StatusTag from '../../components/common/StatusTag';
import { TABLE_TH, TABLE_ROW } from '../../components/layout/FillHeightCard';
import { BTN_SECONDARY_SM } from '../../components/common/ui';
import { CONTROL } from './tableUi';

/** UC1 (daily log + sessions) and UC4 (monthly consolidated) for one employee. */
export interface EmployeeDetailProps {
  employeeId: string;
  /**
   * The work date to open on, `YYYY-MM-DD`. Passed by a surface that is itself showing one day
   * — the presence board with its date filter moved off today — so the drawer answers for the
   * day the operator was reading. Omitted means today.
   */
  initialDate?: string;
  onClose: () => void;
}

/** A work date the drawer can actually fetch: the shape the two date pickers produce. */
const isWorkDate = (v?: string): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * The side drawer the attendance pages open. Its contents are `EmployeeDetailBody`, which the
 * dashboard's Presence card shows in its own modal — one record, two frames, so the two can
 * never disagree about a person's day.
 */
export default function EmployeeDetail({ employeeId, initialDate, onClose }: EmployeeDetailProps) {
  return (
    <div className="fixed inset-0 overlay-backdrop flex justify-end z-50" onClick={onClose}>
      <div className="bg-card w-full max-w-3xl h-full overflow-y-auto shadow-modal border-l border-border p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <EmployeeDetailBody
          employeeId={employeeId}
          initialDate={initialDate}
          headerAction={<button onClick={onClose} className={BTN_SECONDARY_SM}>Close</button>}
        />
      </div>
    </div>
  );
}

export interface EmployeeDetailBodyProps {
  employeeId: string;
  /** As on `EmployeeDetail`: the work date to open on, `YYYY-MM-DD`; omitted means today. */
  initialDate?: string;
  /**
   * The name-and-role header. Off where the frame already titles the record with the person
   * (the dashboard's modal).
   */
  showHeader?: boolean;
  /** Beside the header — the drawer's Close button. */
  headerAction?: ReactNode;
}

/** UC1 (daily log + sessions) and UC4 (monthly consolidated) for one employee, frameless. */
export function EmployeeDetailBody({ employeeId, initialDate, showHeader = true, headerAction }: EmployeeDetailBodyProps) {
  const { attendance } = useApi();
  const [emp, setEmp] = useState<any>(null);
  // Local time, not `toISOString()`: that is the UTC date, so between midnight and 05:30 IST
  // the drawer opened on YESTERDAY's log and called it today.
  const [date, setDate] = useState(() => (isWorkDate(initialDate) ? initialDate : todayStr()));
  // Follows the day, so the monthly table below is the month containing it — a drawer opened on
  // the 3rd of last month showing THIS month's consolidation reads as two different people.
  const [month, setMonth] = useState(() => (isWorkDate(initialDate) ? initialDate.slice(0, 7) : thisMonthStr()));
  const [day, setDay] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [monthly, setMonthly] = useState<any>(null);
  const [sightings, setSightings] = useState<any[]>([]);

  useEffect(() => { (async () => { try { setEmp(await attendance.getEmployee(employeeId)); } catch (e) { console.error(e); } })(); }, [attendance, employeeId]);
  useEffect(() => { (async () => {
    try {
      const d = await attendance.employeeDay(employeeId, date);
      setDay(d?.day || null); setSessions(d?.sessions || []);
      const s = await attendance.employeeSightings(employeeId, { from: `${date}T00:00:00`, to: `${date}T23:59:59`, limit: 200 });
      setSightings(s?.data || []);
    } catch (e) { console.error(e); }
  })(); }, [attendance, employeeId, date]);
  useEffect(() => { (async () => { try { setMonthly(await attendance.employeeMonthly(employeeId, month)); } catch (e) { console.error(e); } })(); }, [attendance, employeeId, month]);

  return (
    <>
      {showHeader && (
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{emp?.name || 'Employee'}</h2>
            {/* Same order as the board that opened this: code, then what they do, then where.
                A drawer that re-orders the person's own sub-line reads as a different person. */}
            <div className="text-sm text-muted-foreground">
              {[emp?.employee_code, emp?.designation, emp?.department].filter(Boolean).join(' · ')}
            </div>
          </div>
          {headerAction}
        </div>
      )}

      {/* ── Daily ── */}
      <section className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-medium text-foreground">Daily log</h3>
          {/* Today is the ceiling — a future day has no log to show. Bound to the
              calendar rather than to the day being viewed, so picking an earlier date
              never narrows what can be picked next. */}
          <input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} aria-label="Day" className={`ml-auto ${CONTROL}`} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <Stat label="First in" value={fmtTime(day?.first_in_at)} />
          <Stat label="Last out" value={fmtTime(day?.last_out_at)} />
          <Stat label="Total inside" value={fmtDuration(day?.total_inside_minutes)} />
          {/* Daily log → Present / Absent; the day-type split lives in Monthly below. */}
          <Stat label="Status" value={<PresenceTag status={day?.status} />} />
        </div>
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-card"><tr className="border-b border-border"><th className={TABLE_TH}>In</th><th className={TABLE_TH}>Out</th><th className={TABLE_TH}>Duration</th></tr></thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No sessions.</td></tr>}
              {sessions.map((s) => (
                <tr key={s.id} className={TABLE_ROW}>
                  <td className="px-3 py-2">{fmtTime(s.in_at)}</td>
                  <td className="px-3 py-2">
                    {s.is_open
                      ? <span className="text-success">Still in</span>
                      : s.out_source === 'event'
                        ? fmtTime(s.out_at)
                        : <span className="text-muted-foreground" title="No exit was recorded by a camera — session auto-closed at the last time they were seen.">≈ {fmtTime(s.out_at)} (no out record)</span>}
                  </td>
                  <td className="px-3 py-2">{fmtDuration(s.duration_minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Raw in/out log: every camera sighting with its resolved direction ── */}
        {sightings.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-foreground mb-2">In/Out log <span className="text-xs text-muted-foreground font-normal">({sightings.length} sightings on {date})</span></h4>
            <div className="rounded-lg border border-border max-h-64 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card"><tr className="border-b border-border"><th className={TABLE_TH}>Time</th><th className={TABLE_TH}>In/Out</th><th className={TABLE_TH}>Camera</th></tr></thead>
                <tbody>
                  {sightings.map((sg) => (
                    <tr key={sg.id} className={TABLE_ROW}>
                      <td className="px-3 py-2">{fmtTime(sg.seen_at)}</td>
                      <td className="px-3 py-2">
                        {sg.inout === 'in' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-success/15 text-success">↓ In</span>}
                        {sg.inout === 'out' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-destructive/15 text-destructive">↑ Out</span>}
                        {sg.inout !== 'in' && sg.inout !== 'out' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-muted text-muted-foreground" title="Camera couldn't determine walking direction">Seen</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{sg.camera_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ── Monthly ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-medium text-foreground">Monthly</h3>
          <input type="month" value={month} max={thisMonthStr()} onChange={(e) => setMonth(e.target.value)} aria-label="Month" className={`ml-auto ${CONTROL}`} />
        </div>
        {/* Consolidated day types — Full day / Half day / Absent, in a fixed order so
            the chips don't reshuffle between months. `partial` days fold into Absent
            because they earn no day credit. */}
        {monthly?.summary && (() => {
          const buckets = consolidateSummary(monthly.summary);
          return (
            <div className="flex gap-2 mb-3 flex-wrap">
              {CONSOLIDATED_BUCKETS.map((b) => (
                <StatusTag key={b} tone={consolidatedTone(b)}>
                  {consolidatedLabel(b)}
                  <span className="ml-0.5 tabular-nums">{buckets[b]}</span>
                </StatusTag>
              ))}
            </div>
          );
        })()}
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-card"><tr className="border-b border-border"><th className={TABLE_TH}>Date</th><th className={TABLE_TH}>First in</th><th className={TABLE_TH}>Last out</th><th className={TABLE_TH}>Total</th><th className={TABLE_TH}>Status</th></tr></thead>
            <tbody>
              {(!monthly?.days || monthly.days.length === 0) && <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No data for this month.</td></tr>}
              {monthly?.days?.map((d: any) => (
                <tr key={d.work_date} className={TABLE_ROW}>
                  <td className="px-3 py-2">{d.work_date}</td>
                  <td className="px-3 py-2">{fmtTime(d.first_in_at)}</td>
                  <td className="px-3 py-2">{fmtTime(d.last_out_at)}</td>
                  <td className="px-3 py-2">{fmtDuration(d.total_inside_minutes)}</td>
                  <td className="px-3 py-2"><DayTypeTag status={d.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** `value` may be text or a ready-made element (e.g. a StatusTag). */
function Stat({ label, value }: { label: string; value: any }) {
  const isEl = typeof value === 'object' && value !== null;
  return (
    <div className="bg-background border border-border rounded-lg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 ${isEl ? '' : 'text-sm font-medium text-foreground'}`}>{value}</div>
    </div>
  );
}
